import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, CheckCircle2 } from "lucide-react";
import api, { LOGO_URL, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters.");
    if (password !== confirm) return toast.error("Passwords do not match.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password reset! You can now sign in.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed.");
    } finally { setLoading(false); }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-accent px-6 grain-bg">
      <div className="w-full max-w-md rounded-3xl border border-border bg-card p-8 shadow-2xl">
        <div className="mb-6 flex flex-col items-center text-center">
          <img src={LOGO_URL} alt="Crystal Pharmacy" className="mb-4 h-12 w-auto" />
          <div className="flex items-center gap-2 text-primary">
            <KeyRound className="h-4 w-4" /><span className="text-sm font-semibold uppercase tracking-[0.2em]">Reset Password</span>
          </div>
        </div>

        {!token ? (
          <p className="text-center text-sm text-muted-foreground" data-testid="reset-no-token">
            This reset link is missing its token. Please use the link from your email or request a new one.
          </p>
        ) : done ? (
          <div className="flex flex-col items-center gap-4 text-center" data-testid="reset-success">
            <CheckCircle2 className="h-12 w-12 text-primary" />
            <p className="text-sm text-muted-foreground">Your password has been updated.</p>
            <Button asChild className="rounded-full px-8"><Link to="/admin/login" data-testid="reset-goto-login">Go to Sign In</Link></Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2"><Label>New password</Label><Input type="password" data-testid="reset-password" value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus /></div>
            <div className="space-y-2"><Label>Confirm password</Label><Input type="password" data-testid="reset-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} required /></div>
            <Button type="submit" disabled={loading} data-testid="reset-submit" className="w-full rounded-full">{loading ? "Resetting..." : "Reset Password"}</Button>
          </form>
        )}
      </div>
    </div>
  );
}
